import React, { useMemo, useState } from 'react';
import {
  STOCK_QUALITY_COLUMNS,
  formatStockQty,
  formatAverageCost,
  formatInventoryValue,
  totalFromCountedByCondition,
  hasQualityAdjustmentDiff,
  hasSimpleAdjustmentDiff,
  hasCostAdjustmentDiff,
} from '../../utils/stockByConditionDisplay';

const num = (v) => {
  const x = parseFloat(v);
  return Number.isFinite(x) ? x : 0;
};

const deltaClass = (d) => {
  if (d === null) return 'text-gray-600';
  if (d > 0) return 'text-green-700';
  if (d < 0) return 'text-red-700';
  return 'text-gray-600';
};

const rowHasAdjustmentDiff = (row, adjustmentMode) => {
  if (adjustmentMode === 'quality') return hasQualityAdjustmentDiff(row);
  if (adjustmentMode === 'cost_only') return hasCostAdjustmentDiff(row);
  return hasSimpleAdjustmentDiff(row);
};

const CostCell = ({ loaded, stockZero, row, onCostChange }) => {
  if (!loaded) return '—';
  if (stockZero) {
    return (
      <input
        type="number"
        min="0"
        step="any"
        className="w-full min-w-[4.5rem] border rounded px-1 py-1 text-right text-sm"
        value={row.averageCostManual ?? ''}
        onChange={(e) => onCostChange(row.product_id, 'averageCostManual', e.target.value)}
        title="Costo unitario para valorizar el ajuste (stock 0)"
      />
    );
  }
  return formatAverageCost(row.averageCost);
};

const CostOnlyRow = React.memo(({ row, onUpdateRow }) => {
  const ref = row.stockAtEntry;
  const loaded = ref !== null && ref !== undefined;
  const hasStock = loaded && Math.abs(num(ref)) >= 0.0001;
  const currentCost = num(row.averageCost);
  const targetRaw = row.targetCostManual;
  const hasTarget =
    targetRaw !== null && targetRaw !== undefined && String(targetRaw).trim() !== '';
  const targetCost = hasTarget ? num(targetRaw) : null;
  const costDelta =
    loaded && hasStock && hasTarget
      ? Math.round((targetCost - currentCost) * 10000) / 10000
      : null;
  const valueBefore = loaded && hasStock ? ref * currentCost : null;
  const valueAfter =
    loaded && hasStock && hasTarget ? ref * targetCost : null;
  const valueDelta =
    valueBefore !== null && valueAfter !== null
      ? Math.round((valueAfter - valueBefore) * 100) / 100
      : null;

  return (
    <tr className={`border-t border-gray-100 hover:bg-gray-50 ${row.productMissing ? 'bg-red-50/40' : ''}`}>
      <td className="px-3 py-2 font-mono text-xs">{row.sku}</td>
      <td className="px-3 py-2">
        {row.name}
        {row.productMissing ? (
          <span className="ml-1 text-xs text-red-600" title="Sin ficha en catálogo; no se puede ajustar costo">
            (sin catálogo)
          </span>
        ) : null}
      </td>
      <td className="px-3 py-2">{row.unit_of_measure}</td>
      <td className="px-2 py-2 text-right tabular-nums">{loaded ? formatStockQty(ref) : '—'}</td>
      <td className="px-2 py-2 text-right tabular-nums text-gray-800">
        {loaded && hasStock ? formatAverageCost(currentCost) : loaded ? '—' : '—'}
      </td>
      <td className="px-1 py-1 bg-violet-50/40">
        {loaded && hasStock && !row.productMissing ? (
          <input
            type="number"
            min="0"
            step="any"
            className="w-full min-w-[4.5rem] border rounded px-1 py-1 text-right text-sm"
            value={row.targetCostManual ?? ''}
            onChange={(e) => onUpdateRow(row.product_id, 'targetCostManual', e.target.value)}
            title="Costo promedio objetivo"
          />
        ) : (
          <span className="text-xs text-gray-400">Sin stock</span>
        )}
      </td>
      <td className={`px-2 py-2 text-right tabular-nums text-xs font-medium ${deltaClass(costDelta)}`}>
        {costDelta === null ? '—' : formatAverageCost(costDelta)}
      </td>
      <td className="px-2 py-2 text-right tabular-nums text-xs">
        {valueBefore === null ? '—' : formatInventoryValue(ref, currentCost)}
      </td>
      <td className="px-2 py-2 text-right tabular-nums text-xs bg-violet-50/30">
        {valueAfter === null ? '—' : formatInventoryValue(ref, targetCost)}
      </td>
      <td className={`px-2 py-2 text-right tabular-nums text-xs font-medium ${deltaClass(valueDelta)}`}>
        {valueDelta === null ? '—' : valueDelta.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </td>
    </tr>
  );
});

CostOnlyRow.displayName = 'CostOnlyRow';

const QualityRow = React.memo(({ row, onUpdateRow, onUpdateRowCountedCond }) => {
  const ref = row.stockAtEntry;
  const loaded = ref !== null && ref !== undefined;
  const stockZero = loaded && Math.abs(num(ref)) < 0.0001;
  const cbc = row.countedByCondition;
  const totalCounted = loaded && cbc ? totalFromCountedByCondition(cbc) : 0;
  const totalDelta =
    loaded && ref !== undefined ? Math.round((totalCounted - ref) * 1000) / 1000 : null;

  return (
    <tr className="border-t border-gray-100 hover:bg-gray-50">
      <td className="px-3 py-2 font-mono text-xs">{row.sku}</td>
      <td className="px-3 py-2">{row.name}</td>
      <td className="px-3 py-2">{row.unit_of_measure}</td>
      <td className="px-2 py-2 text-right tabular-nums">{loaded ? formatStockQty(ref) : '—'}</td>
      {STOCK_QUALITY_COLUMNS.map(({ key }) => (
        <td key={key} className="px-1.5 py-2 text-right text-xs tabular-nums text-gray-800">
          {loaded && row.stockByCondition ? formatStockQty(row.stockByCondition[key] ?? 0) : '—'}
        </td>
      ))}
      <td className="px-2 py-2 text-right tabular-nums">
        <CostCell loaded={loaded} stockZero={stockZero} row={row} onCostChange={onUpdateRow} />
      </td>
      <td className="px-2 py-2 text-right tabular-nums text-xs bg-amber-50/30">
        {loaded && cbc ? formatStockQty(totalCounted) : '—'}
      </td>
      {STOCK_QUALITY_COLUMNS.map(({ key }) => (
        <td key={`in-${key}`} className="px-1 py-1 bg-amber-50/30">
          <input
            type="number"
            step="any"
            className="w-full min-w-[3.25rem] border rounded px-1 py-1 text-right text-xs"
            value={cbc ? cbc[key] ?? '' : ''}
            onChange={(e) => onUpdateRowCountedCond(row.product_id, key, e.target.value)}
            disabled={!loaded}
          />
        </td>
      ))}
      <td className={`px-2 py-2 text-right font-medium text-xs tabular-nums ${deltaClass(totalDelta)}`}>
        {totalDelta === null ? '—' : totalDelta}
      </td>
      {STOCK_QUALITY_COLUMNS.map(({ key }) => {
        const d =
          loaded && row.stockByCondition && cbc
            ? Math.round((num(cbc[key]) - num(row.stockByCondition[key])) * 1000) / 1000
            : null;
        return (
          <td
            key={`del-${key}`}
            className={`px-1.5 py-2 text-right text-xs font-medium tabular-nums ${deltaClass(d)}`}
          >
            {d === null ? '—' : d}
          </td>
        );
      })}
    </tr>
  );
});

QualityRow.displayName = 'QualityRow';

const SimpleRow = React.memo(({ row, onUpdateRow, onUpdateRowCountedSimple }) => {
  const ref = row.stockAtEntry;
  const loaded = ref !== null && ref !== undefined;
  const stockZero = loaded && Math.abs(num(ref)) < 0.0001;
  const cbc = row.countedByCondition;
  const totalCounted = cbc ? totalFromCountedByCondition(cbc) : 0;
  const totalDelta =
    loaded && ref !== undefined ? Math.round((totalCounted - ref) * 1000) / 1000 : null;

  return (
    <tr className="border-t border-gray-100 hover:bg-gray-50">
      <td className="px-3 py-2 font-mono text-xs">{row.sku}</td>
      <td className="px-3 py-2">{row.name}</td>
      <td className="px-3 py-2">{row.unit_of_measure}</td>
      <td className="px-2 py-2 text-right tabular-nums">{loaded ? formatStockQty(ref) : '—'}</td>
      <td className="px-2 py-2 text-right tabular-nums">
        <CostCell loaded={loaded} stockZero={stockZero} row={row} onCostChange={onUpdateRow} />
      </td>
      <td className="px-1 py-1 bg-amber-50/30">
        <input
          type="number"
          step="any"
          className="w-full min-w-[4rem] border rounded px-1 py-1 text-right text-sm"
          value={cbc?.good ?? ''}
          onChange={(e) => onUpdateRowCountedSimple(row.product_id, e.target.value)}
          disabled={!loaded}
        />
      </td>
      <td className={`px-2 py-2 text-right font-medium tabular-nums ${deltaClass(totalDelta)}`}>
        {totalDelta === null ? '—' : totalDelta}
      </td>
    </tr>
  );
});

SimpleRow.displayName = 'SimpleRow';

const AdjustmentGuideItemsPanel = ({
  search,
  onSearchChange,
  filteredRows,
  totalRows,
  countLabel,
  adjustmentMode,
  onAdjustmentModeChange,
  excelBusy,
  onExportExcel,
  importExcelRef,
  onImportExcel,
  exportDisabled = false,
  onUpdateRow,
  onUpdateRowCountedCond,
  onUpdateRowCountedSimple,
  deferFullList = false,
  stockLoaded = false,
}) => {
  const [onlyWithStock, setOnlyWithStock] = useState(false);
  const [onlyModified, setOnlyModified] = useState(false);
  const isCostOnly = adjustmentMode === 'cost_only';
  const isQuality = adjustmentMode === 'quality';

  const displayRows = useMemo(() => {
    let list = filteredRows;
    if (onlyWithStock || isCostOnly) {
      list = list.filter((r) => r.stockAtEntry !== null && r.stockAtEntry !== undefined);
    }
    if (isCostOnly) {
      list = list.filter((r) => Math.abs(num(r.stockAtEntry)) >= 0.0001);
    }
    if (onlyModified) {
      list = list.filter((r) => rowHasAdjustmentDiff(r, adjustmentMode));
    }
    return list;
  }, [filteredRows, onlyWithStock, onlyModified, adjustmentMode, isCostOnly]);

  const modifiedCount = useMemo(
    () => filteredRows.filter((r) => rowHasAdjustmentDiff(r, adjustmentMode)).length,
    [filteredRows, adjustmentMode]
  );

  const shouldDeferList =
    deferFullList &&
    !stockLoaded &&
    !search.trim() &&
    !onlyWithStock &&
    !onlyModified &&
    !isCostOnly &&
    displayRows.length > 100;

  const visibleRows = shouldDeferList ? [] : displayRows;

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="p-4 border-b flex flex-wrap gap-3 justify-between items-center">
        <input
          type="search"
          placeholder="Buscar por nombre o SKU…"
          className="border rounded-md px-3 py-2 flex-1 min-w-[200px]"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
        <div className="flex flex-wrap items-center gap-2">
          {!isCostOnly && (
            <label className="flex items-center gap-1.5 text-xs text-gray-600 whitespace-nowrap cursor-pointer">
              <input
                type="checkbox"
                checked={onlyWithStock}
                onChange={(e) => setOnlyWithStock(e.target.checked)}
                className="rounded border-gray-300"
              />
              Solo con stock cargado
            </label>
          )}
          <label
            className="flex items-center gap-1.5 text-xs text-gray-600 whitespace-nowrap cursor-pointer"
            title="Muestra solo productos con cambios respecto al sistema (los que se incluirán en la guía)"
          >
            <input
              type="checkbox"
              checked={onlyModified}
              onChange={(e) => setOnlyModified(e.target.checked)}
              className="rounded border-gray-300"
            />
            Solo con ajuste
          </label>
          <div
            className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5 text-xs sm:text-sm flex-wrap"
            role="group"
            aria-label="Modo de ajuste"
          >
            <button
              type="button"
              onClick={() => onAdjustmentModeChange('quality')}
              className={`rounded-md px-2.5 py-1.5 font-medium transition-colors ${
                isQuality ? 'bg-teal-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Por calidad
            </button>
            <button
              type="button"
              onClick={() => onAdjustmentModeChange('simple')}
              className={`rounded-md px-2.5 py-1.5 font-medium transition-colors ${
                adjustmentMode === 'simple' ? 'bg-teal-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Ajuste simple
            </button>
            <button
              type="button"
              onClick={() => onAdjustmentModeChange('cost_only')}
              className={`rounded-md px-2.5 py-1.5 font-medium transition-colors ${
                isCostOnly ? 'bg-violet-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Solo costos
            </button>
          </div>
          <span className="text-sm text-gray-500 whitespace-nowrap">
            {displayRows.length} en vista / {totalRows} {countLabel}
            {modifiedCount > 0 && (
              <span className="text-teal-700 font-medium"> · {modifiedCount} con ajuste</span>
            )}
          </span>
          {!isCostOnly && (
            <>
              <button
                type="button"
                onClick={onExportExcel}
                disabled={excelBusy || exportDisabled}
                className="px-3 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50 whitespace-nowrap"
              >
                {excelBusy ? '…' : 'Exportar Excel'}
              </button>
              <button
                type="button"
                onClick={() => importExcelRef.current?.click()}
                disabled={excelBusy}
                className="px-3 py-2 text-sm bg-white border border-gray-300 text-gray-800 rounded-md hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
              >
                Importar solo conteo
              </button>
              <input
                ref={importExcelRef}
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                onChange={onImportExcel}
              />
            </>
          )}
        </div>
      </div>

      {adjustmentMode === 'simple' && (
        <p className="px-4 py-2 text-xs text-gray-600 bg-teal-50/60 border-b border-teal-100">
          Modo simple: solo total y costo promedio. Todo el conteo físico se registra como{' '}
          <strong>Bueno</strong>. Solo se ajustan productos con conteo distinto al stock del sistema.
        </p>
      )}

      {isCostOnly && (
        <p className="px-4 py-2 text-xs text-gray-600 bg-violet-50/60 border-b border-violet-100">
          Modo solo costos: indique el costo promedio objetivo por producto con stock. El sistema calcula el ajuste de
          valor en kardex sin modificar cantidades.
        </p>
      )}

      {shouldDeferList && (
        <div className="px-4 py-8 text-center text-sm text-gray-600 border-b border-gray-100">
          <p className="font-medium text-gray-800">{totalRows} productos en el almacén</p>
          <p className="mt-1">
            Use <strong>Buscar</strong>, active <strong>Solo con stock cargado</strong> después de cargar stock, o
            pulse <strong>Cargar stock</strong> para trabajar sin colgar el navegador.
          </p>
        </div>
      )}

      {!shouldDeferList && totalRows === 0 && (
        <div className="px-4 py-8 text-center text-sm text-gray-600 border-b border-gray-100">
          <p className="font-medium text-gray-800">No hay productos para este almacén</p>
          <p className="mt-1">
            Pulse <strong>Cargar stock</strong> con fecha y hora de ingreso. Si sigue vacío, revise que existan
            productos activos y, si aplica, su asignación al almacén en Configuración general.
          </p>
        </div>
      )}

      {!shouldDeferList && displayRows.length === 0 && onlyModified && (
        <div className="px-4 py-6 text-center text-sm text-gray-600 border-b border-gray-100">
          <p className="font-medium text-gray-800">Ningún producto con ajuste en esta vista</p>
          <p className="mt-1">
            Active el filtro después de cargar stock e indicar los cambios. Solo aparecen ítems que se incluirán en la
            guía.
          </p>
        </div>
      )}

      {!shouldDeferList && !(displayRows.length === 0 && onlyModified) && (
        <>
          <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
            {isCostOnly ? (
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr className="border-b border-gray-200 text-xs">
                    <th className="text-left px-3 py-2">SKU</th>
                    <th className="text-left px-3 py-2 min-w-[140px]">Producto</th>
                    <th className="text-left px-3 py-2">UM</th>
                    <th className="text-right px-2 py-2">Stock</th>
                    <th className="text-right px-2 py-2 whitespace-nowrap">Costo actual</th>
                    <th className="text-right px-2 py-2 bg-violet-50/80 whitespace-nowrap">Nuevo costo</th>
                    <th className="text-right px-2 py-2 whitespace-nowrap">Δ costo</th>
                    <th className="text-right px-2 py-2 whitespace-nowrap">Valor actual</th>
                    <th className="text-right px-2 py-2 bg-violet-50/80 whitespace-nowrap">Valor nuevo</th>
                    <th className="text-right px-2 py-2 whitespace-nowrap">Δ valor</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((r) => (
                    <CostOnlyRow key={r.product_id} row={r} onUpdateRow={onUpdateRow} />
                  ))}
                </tbody>
              </table>
            ) : isQuality ? (
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr className="border-b border-gray-200">
                    <th rowSpan={2} className="text-left px-3 py-2 align-bottom">
                      SKU
                    </th>
                    <th rowSpan={2} className="text-left px-3 py-2 align-bottom min-w-[140px]">
                      Producto
                    </th>
                    <th rowSpan={2} className="text-left px-3 py-2 align-bottom">
                      UM
                    </th>
                    <th
                      colSpan={1 + STOCK_QUALITY_COLUMNS.length}
                      className="text-center px-2 py-1 text-xs font-semibold text-teal-800 bg-teal-50 border-b border-teal-100"
                    >
                      Stock sistema (por calidad)
                    </th>
                    <th rowSpan={2} className="text-right px-2 py-2 align-bottom whitespace-nowrap max-w-[7rem]">
                      Costo prom.
                    </th>
                    <th
                      colSpan={1 + STOCK_QUALITY_COLUMNS.length}
                      className="text-center px-2 py-1 text-xs font-semibold text-amber-900 bg-amber-50 border-b border-amber-100"
                    >
                      Conteo físico
                    </th>
                    <th
                      colSpan={1 + STOCK_QUALITY_COLUMNS.length}
                      className="text-center px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-100 border-b border-gray-200"
                    >
                      Δ
                    </th>
                  </tr>
                  <tr className="bg-gray-100 text-xs">
                    <th className="text-right px-2 py-2 font-medium text-gray-700 border-b">Total</th>
                    {STOCK_QUALITY_COLUMNS.map(({ key, label }) => (
                      <th key={`s-${key}`} title={label} className="text-right px-1.5 py-2 font-medium text-gray-700 border-b whitespace-nowrap">
                        {label}
                      </th>
                    ))}
                    <th className="text-right px-2 py-2 font-medium text-gray-700 border-b bg-amber-50/50">Total</th>
                    {STOCK_QUALITY_COLUMNS.map(({ key, label }) => (
                      <th key={`c-${key}`} title={label} className="text-right px-1.5 py-2 font-medium text-gray-700 border-b whitespace-nowrap bg-amber-50/50">
                        {label}
                      </th>
                    ))}
                    <th className="text-right px-2 py-2 font-medium text-gray-700 border-b">Total</th>
                    {STOCK_QUALITY_COLUMNS.map(({ key, label }) => (
                      <th key={`d-${key}`} title={label} className="text-right px-1.5 py-2 font-medium text-gray-700 border-b whitespace-nowrap">
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((r) => (
                    <QualityRow
                      key={r.product_id}
                      row={r}
                      onUpdateRow={onUpdateRow}
                      onUpdateRowCountedCond={onUpdateRowCountedCond}
                    />
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr className="border-b border-gray-200 text-xs">
                    <th className="text-left px-3 py-2">SKU</th>
                    <th className="text-left px-3 py-2 min-w-[140px]">Producto</th>
                    <th className="text-left px-3 py-2">UM</th>
                    <th className="text-right px-2 py-2">Stock sistema</th>
                    <th className="text-right px-2 py-2 whitespace-nowrap">Costo prom.</th>
                    <th className="text-right px-2 py-2 bg-amber-50/80">Conteo físico</th>
                    <th className="text-right px-2 py-2">Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((r) => (
                    <SimpleRow
                      key={r.product_id}
                      row={r}
                      onUpdateRow={onUpdateRow}
                      onUpdateRowCountedSimple={onUpdateRowCountedSimple}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AdjustmentGuideItemsPanel;
